// pages/ForwardingOptions.jsx
import React, { useState, useEffect } from "react";
import { useOutletContext } from "react-router-dom";
import Header from "../components/Header";
import ForwardingOverviewModal from "../components/ForwardingOverviewModal";
import { Button, Col, Row, Form, Card, Alert } from "react-bootstrap";
import { FiShare2, FiEye } from "react-icons/fi";
import Select from "react-select";
import { getHost, defaultClientId } from "../utils/utils";

// Match the dark, transparent look of the rest of the dashboard inputs.
const siteSelectStyles = {
  control: (base, state) => ({
    ...base,
    backgroundColor: "transparent",
    borderColor: state.isFocused ? "#7C98FF" : "rgba(255,255,255,0.15)",
    boxShadow: "none",
    minHeight: 40,
    color: "white",
  }),
  valueContainer: (base) => ({ ...base, padding: "2px 6px" }),
  multiValue: (base) => ({
    ...base,
    backgroundColor: "rgba(124,152,255,0.15)",
    borderRadius: 6,
  }),
  multiValueLabel: (base) => ({ ...base, color: "white" }),
  multiValueRemove: (base) => ({
    ...base,
    color: "white",
    ":hover": { backgroundColor: "rgba(255,87,123,0.4)", color: "white" },
  }),
  menu: (base) => ({
    ...base,
    backgroundColor: "#1a1a1a",
    border: "1px solid rgba(255,255,255,0.1)",
  }),
  option: (base, state) => ({
    ...base,
    backgroundColor: state.isFocused ? "rgba(124,152,255,0.15)" : "transparent",
    color: "white",
    cursor: "pointer",
  }),
  input: (base) => ({ ...base, color: "white" }),
  placeholder: (base) => ({ ...base, color: "rgba(255,255,255,0.45)" }),
  singleValue: (base) => ({ ...base, color: "white" }),
};

const API_CLIENTS = `${getHost()}/api/clients`;
const getToken = () => localStorage.getItem("jwt");

// Empty/clear state for the router-based forward form.
// `match_monitors` is an array of code_keys (driven by the site multi-select);
// the other filter fields stay as raw comma-separated strings until submit.
const EMPTY_ROUTER_FORM = {
  client_id: "",
  webhook_url: "",
  keywords: "",
  match_regions: "",
  match_monitors: [],
  match_pids: "",
  match_sizes: "",
  exclude_domains: "",
  max_price: "",
};

const ForwardingOptions = () => {
  const { toggleSidebar } = useOutletContext();
  const [clients, setClients] = useState([]);
  const [showOverview, setShowOverview] = useState(false);

  // Card 1: Channel Forward
  const [channelForward, setChannelForward] = useState({
    client_id: "",
    channel_id: "",
    webhook_url: "",
    excluded_domains: "",
  });
  const [channelMessage, setChannelMessage] = useState("");

  // Card 2: Channel Keyword Forward
  const [channelKeywordForward, setChannelKeywordForward] = useState({
    client_id: "",
    channel_id: "",
    keywords: "",
    webhook_url: "",
    excluded_domains: "",
  });
  const [channelKeywordMessage, setChannelKeywordMessage] = useState("");

  // Card 3: Router-based keyword forward (new system, writes to keywordforwards).
  const [monitorList, setMonitorList] = useState([]); // for the Sites multi-select
  const [routerForward, setRouterForward] = useState(EMPTY_ROUTER_FORM);
  const [routerMessage, setRouterMessage] = useState("");
  const [routerSubmitting, setRouterSubmitting] = useState(false);

  // Fetch clients on mount
  useEffect(() => {
    fetch(API_CLIENTS, {
      headers: { Authorization: `Bearer ${getToken()}` },
    })
      .then((res) => res.json())
      .then((data) => {
        setClients(data.clients || []);
      })
      .catch(() => console.error("Failed to load clients"));
  }, []);

  // Pull the monitor list for the Sites multi-select. Display label = `name`,
  // value sent to backend = `code_key` (falls back to `folder_name`).
  // Sorted alphabetically.
  useEffect(() => {
    fetch(`${getHost()}/api/monitors`, {
      headers: { Authorization: `Bearer ${getToken()}` },
    })
      .then((res) => res.json())
      .then((data) => {
        const ms = (data.monitors || [])
          .filter((m) => m.folder_name)
          .sort((a, b) => (a.name || "").localeCompare(b.name || ""));
        setMonitorList(ms);
      })
      .catch(() => setMonitorList([]));
  }, []);

  // Auto-select the only group if there's just one
  useEffect(() => {
    const def = defaultClientId(clients);
    if (!def) return;
    setChannelForward((prev) =>
      prev.client_id ? prev : { ...prev, client_id: def },
    );
    setChannelKeywordForward((prev) =>
      prev.client_id ? prev : { ...prev, client_id: def },
    );
    setRouterForward((prev) =>
      prev.client_id ? prev : { ...prev, client_id: def },
    );
  }, [clients]);

  // Handle Channel Forward Submit
  const handleChannelForwardSubmit = (e) => {
    e.preventDefault();
    setChannelMessage("");

    const domainsArray = channelForward.excluded_domains
      .split(",")
      .map((d) => d.trim().toLowerCase())
      .filter((d) => d);

    fetch(`${getHost()}/api/forwarding/channel`, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${getToken()}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        ...channelForward,
        excluded_domains: domainsArray,
      }),
    })
      .then(async (res) => {
        const data = await res.json();
        if (!res.ok) throw new Error(data.error);
        setChannelMessage("✅ Channel forward added successfully!");
        setChannelForward({
          client_id: defaultClientId(clients),
          channel_id: "",
          webhook_url: "",
          excluded_domains: "",
        });
      })
      .catch((err) => {
        setChannelMessage(`❌ ${err.message || "Failed to add forward"}`);
      });
  };

  // Handle Channel Keyword Forward Submit
  const handleChannelKeywordForwardSubmit = (e) => {
    e.preventDefault();
    setChannelKeywordMessage("");

    const keywordsArray = channelKeywordForward.keywords
      .split(",")
      .map((k) => k.trim())
      .filter((k) => k);

    const domainsArray = channelKeywordForward.excluded_domains
      .split(",")
      .map((d) => d.trim().toLowerCase())
      .filter((d) => d);

    fetch(`${getHost()}/api/forwarding/channel-keyword`, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${getToken()}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        ...channelKeywordForward,
        keywords: keywordsArray,
        excluded_domains: domainsArray,
      }),
    })
      .then(async (res) => {
        const data = await res.json();
        if (!res.ok) throw new Error(data.error);
        setChannelKeywordMessage(
          "✅ Channel keyword forward added successfully!",
        );
        setChannelKeywordForward({
          client_id: defaultClientId(clients),
          channel_id: "",
          keywords: "",
          webhook_url: "",
          excluded_domains: "",
        });
      })
      .catch((err) => {
        setChannelKeywordMessage(
          `❌ ${err.message || "Failed to add forward"}`,
        );
      });
  };

  // Card 4 — Router-based keyword forward (writes to keywordforwards)
  const handleRouterForwardSubmit = (e) => {
    e.preventDefault();
    setRouterMessage("");

    if (!routerForward.client_id || !routerForward.webhook_url) {
      setRouterMessage("❌ Client and webhook URL are required.");
      return;
    }

    const parseList = (s) =>
      (s || "")
        .split(",")
        .map((v) => v.trim())
        .filter(Boolean);

    const payload = {
      client_id: routerForward.client_id,
      webhook_url: routerForward.webhook_url.trim(),
    };
    const keywordList = parseList(routerForward.keywords);
    if (keywordList.length) {
      payload.keywords = keywordList;
    }
    // Sites multi-select. "Select all" = same semantics as none (no filter),
    // so we OMIT the field when the user picked every available site — keeps
    // the Mongo doc small + the router happy. The 320+ string array only goes
    // in the payload when it's a real subset.
    if (
      Array.isArray(routerForward.match_monitors) &&
      routerForward.match_monitors.length > 0 &&
      routerForward.match_monitors.length < monitorList.length
    ) {
      payload.match_monitors = routerForward.match_monitors;
    }
    // Free-text comma fields → string[] on submit.
    for (const f of ["match_regions", "match_pids", "match_sizes", "exclude_domains"]) {
      const list = parseList(routerForward[f]);
      if (list.length) payload[f] = list;
    }
    if (
      routerForward.max_price !== "" &&
      !Number.isNaN(Number(routerForward.max_price))
    ) {
      payload.max_price = Number(routerForward.max_price);
    }

    setRouterSubmitting(true);
    fetch(`${getHost()}/api/forwarding/keyword`, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${getToken()}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify(payload),
    })
      .then(async (res) => {
        const data = await res.json();
        if (!res.ok) throw new Error(data.error || "Failed to add forward");
        setRouterMessage("✅ Keyword forward added successfully!");
        setRouterForward({
          ...EMPTY_ROUTER_FORM,
          client_id: defaultClientId(clients),
        });
      })
      .catch((err) => {
        setRouterMessage(`❌ ${err.message || "Failed to add forward"}`);
      })
      .finally(() => setRouterSubmitting(false));
  };

  return (
    <div className="w-100 dashboard-setting">
      <Header toggleSidebar={toggleSidebar} />
      <div className="px-4 pb-3 content-body">
        {/* Header */}
        <div className="mb-3 d-flex justify-content-between align-items-center">
          <div>
            <div className="d-inline-flex align-items-center justify-content-center bg-dark rounded p-2 mb-2">
              <FiShare2 className="text-white" />
            </div>
            <h2 className="text-main fs-20 fw-600 mb-1">Forwarding Options</h2>
            <p className="text-white-65 fs-14 mb-0">
              Configure your message forwarding rules
            </p>
          </div>

          {/* Overview Button */}
          <Button
            variant="outline-primary"
            className="d-flex align-items-center gap-2"
            onClick={() => setShowOverview(true)}
          >
            <FiEye />
            Overview
          </Button>
        </div>

        <Row className="g-4">
          {/* Card 1: Channel Forward */}
          <Col lg={4}>
            <Card
              className="bg-transparent rounded-3"
              style={{
                border: "1px solid rgba(255, 255, 255, 0.1)",
                boxShadow: "0 0 20px rgba(0, 0, 0, 0.5)",
              }}
            >
              <Card.Body className="p-4">
                <div className="mb-3">
                  <h5 className="text-main fs-16 fw-600 mb-0">
                    Channel Forwarder
                  </h5>
                </div>

                <Alert variant="warning" className="mb-3 py-2">
                  <small>
                    ⚠️This forwarder should only be used for{" "}
                    <strong>checkout logs.</strong>
                  </small>
                </Alert>

                {channelMessage && (
                  <Alert
                    variant={
                      channelMessage.includes("✅") ? "success" : "danger"
                    }
                    className="mb-3"
                  >
                    {channelMessage}
                  </Alert>
                )}

                <Form onSubmit={handleChannelForwardSubmit}>
                  <Form.Group className="mb-3">
                    <Form.Label className="text-main fs-13 fw-500 mb-1">
                      Client
                    </Form.Label>
                    <Form.Select
                      value={channelForward.client_id}
                      onChange={(e) =>
                        setChannelForward({
                          ...channelForward,
                          client_id: e.target.value,
                        })
                      }
                      className="form-input-field rounded-3 text-white-65 custom-select-white py-2 px-3"
                      required
                    >
                      <option value="">Select a group...</option>
                      {clients.map((cl) => (
                        <option key={cl._id} value={cl._id}>
                          {cl.name}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Label className="text-main fs-13 fw-500 mb-1">
                      Channel ID
                    </Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="746216741278267471427676"
                      value={channelForward.channel_id}
                      onChange={(e) =>
                        setChannelForward({
                          ...channelForward,
                          channel_id: e.target.value,
                        })
                      }
                      className="form-input-field border rounded py-2 px-3"
                      required
                    />
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Label className="text-main fs-13 fw-500 mb-1">
                      Webhook URL
                    </Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="https://discord.com/api/webhooks/..."
                      value={channelForward.webhook_url}
                      onChange={(e) =>
                        setChannelForward({
                          ...channelForward,
                          webhook_url: e.target.value,
                        })
                      }
                      className="form-input-field border rounded py-2 px-3"
                      required
                    />
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Label className="text-main fs-13 fw-500 mb-1">
                      Excluded Domains (Optional)
                    </Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="amazon.com, ebay.de (comma-separated)"
                      value={channelForward.excluded_domains}
                      onChange={(e) =>
                        setChannelForward({
                          ...channelForward,
                          excluded_domains: e.target.value,
                        })
                      }
                      className="form-input-field border rounded py-2 px-3"
                    />
                  </Form.Group>

                  <div className="d-flex gap-2 mt-4">
                    <Button
                      variant="secondary"
                      className="rounded-3 py-2 px-3 fw-600 fs-12"
                      onClick={() =>
                        setChannelForward({
                          client_id: defaultClientId(clients),
                          channel_id: "",
                          webhook_url: "",
                          excluded_domains: "",
                        })
                      }
                    >
                      Cancel
                    </Button>
                    <Button
                      type="submit"
                      className="btn-primary rounded-3 py-2 px-3 fw-600 fs-12"
                      style={{
                        backgroundColor: "#4A4A4A",
                        borderColor: "#4A4A4A",
                        color: "white",
                      }}
                    >
                      Create
                    </Button>
                  </div>
                </Form>
              </Card.Body>
            </Card>
          </Col>

          {/* Card 2: Channel Keyword Forward */}
          <Col lg={4}>
            <Card
              className="bg-transparent rounded-3"
              style={{
                border: "1px solid rgba(255, 255, 255, 0.1)",
                boxShadow: "0 0 20px rgba(0, 0, 0, 0.5)",
              }}
            >
              <Card.Body className="p-4">
                <div className="mb-3">
                  <h5 className="text-main fs-16 fw-600 mb-0">
                    Channel + Keyword Forwarder
                  </h5>
                </div>

                {channelKeywordMessage && (
                  <Alert
                    variant={
                      channelKeywordMessage.includes("✅")
                        ? "success"
                        : "danger"
                    }
                    className="mb-3"
                  >
                    {channelKeywordMessage}
                  </Alert>
                )}

                <Form onSubmit={handleChannelKeywordForwardSubmit}>
                  <Form.Group className="mb-3">
                    <Form.Label className="text-main fs-13 fw-500 mb-1">
                      Client
                    </Form.Label>
                    <Form.Select
                      value={channelKeywordForward.client_id}
                      onChange={(e) =>
                        setChannelKeywordForward({
                          ...channelKeywordForward,
                          client_id: e.target.value,
                        })
                      }
                      className="form-input-field rounded-3 text-white-65 custom-select-white py-2 px-3"
                      required
                    >
                      <option value="">Select a group...</option>
                      {clients.map((cl) => (
                        <option key={cl._id} value={cl._id}>
                          {cl.name}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Label className="text-main fs-13 fw-500 mb-1">
                      Channel ID <strong>(from TSB Discord)</strong>
                    </Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="746216741278267471427676"
                      value={channelKeywordForward.channel_id}
                      onChange={(e) =>
                        setChannelKeywordForward({
                          ...channelKeywordForward,
                          channel_id: e.target.value,
                        })
                      }
                      className="form-input-field border rounded py-2 px-3"
                      required
                    />
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Label className="text-main fs-13 fw-500 mb-1">
                      Keywords
                    </Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="ugg, tazz, lowmel (comma-separated)"
                      value={channelKeywordForward.keywords}
                      onChange={(e) =>
                        setChannelKeywordForward({
                          ...channelKeywordForward,
                          keywords: e.target.value,
                        })
                      }
                      className="form-input-field border rounded py-2 px-3"
                      required
                    />
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Label className="text-main fs-13 fw-500 mb-1">
                      Webhook URL
                    </Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="https://discord.com/api/webhooks/..."
                      value={channelKeywordForward.webhook_url}
                      onChange={(e) =>
                        setChannelKeywordForward({
                          ...channelKeywordForward,
                          webhook_url: e.target.value,
                        })
                      }
                      className="form-input-field border rounded py-2 px-3"
                      required
                    />
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Label className="text-main fs-13 fw-500 mb-1">
                      Excluded Domains (Optional)
                    </Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="amazon.com, ebay.de (comma-separated)"
                      value={channelKeywordForward.excluded_domains}
                      onChange={(e) =>
                        setChannelKeywordForward({
                          ...channelKeywordForward,
                          excluded_domains: e.target.value,
                        })
                      }
                      className="form-input-field border rounded py-2 px-3"
                    />
                  </Form.Group>

                  <div className="d-flex gap-2 mt-4">
                    <Button
                      variant="secondary"
                      className="rounded-3 py-2 px-3 fw-600 fs-12"
                      onClick={() =>
                        setChannelKeywordForward({
                          client_id: defaultClientId(clients),
                          channel_id: "",
                          keywords: "",
                          webhook_url: "",
                          excluded_domains: "",
                        })
                      }
                    >
                      Cancel
                    </Button>
                    <Button
                      type="submit"
                      className="btn-primary rounded-3 py-2 px-3 fw-600 fs-12"
                      style={{
                        backgroundColor: "#4A4A4A",
                        borderColor: "#4A4A4A",
                        color: "white",
                      }}
                    >
                      Create
                    </Button>
                  </div>
                </Form>
              </Card.Body>
            </Card>
          </Col>

        </Row>

        {/* Router-based keyword forward (writes to keywordforwards). */}
        <Row className="g-4 mt-2">
          <Col xs={12}>
            <Card
              className="bg-transparent rounded-3"
              style={{
                border: "1px solid rgba(255, 255, 255, 0.1)",
                boxShadow: "0 0 20px rgba(0, 0, 0, 0.5)",
                }}
              >
                <Card.Body className="p-4">
                  <div className="mb-3 d-flex align-items-center gap-2">
                    <h5 className="text-main fs-16 fw-600 mb-0">
                      Keyword Forwarder
                    </h5>
                  </div>

                  {routerMessage && (
                    <Alert
                      variant={routerMessage.includes("✅") ? "success" : "danger"}
                      className="mb-3"
                    >
                      {routerMessage}
                    </Alert>
                  )}

                  <Form onSubmit={handleRouterForwardSubmit}>
                    <Row>
                      <Col md={6}>
                        <Form.Group className="mb-3">
                          <Form.Label className="text-main fs-13 fw-500 mb-1">
                            Client
                          </Form.Label>
                          <Form.Select
                            value={routerForward.client_id}
                            onChange={(e) =>
                              setRouterForward({
                                ...routerForward,
                                client_id: e.target.value,
                              })
                            }
                            className="form-input-field rounded-3 text-white-65 custom-select-white py-2 px-3"
                            required
                          >
                            <option value="">Select a group...</option>
                            {clients.map((cl) => (
                              <option key={cl._id} value={cl._id}>
                                {cl.name}
                              </option>
                            ))}
                          </Form.Select>
                        </Form.Group>

                        <Form.Group className="mb-3">
                          <Form.Label className="text-main fs-13 fw-500 mb-1">
                            Webhook URL
                          </Form.Label>
                          <Form.Control
                            type="text"
                            placeholder="https://discord.com/api/webhooks/..."
                            value={routerForward.webhook_url}
                            onChange={(e) =>
                              setRouterForward({
                                ...routerForward,
                                webhook_url: e.target.value,
                              })
                            }
                            className="form-input-field border rounded py-2 px-3"
                            required
                          />
                        </Form.Group>

                        <Form.Group className="mb-3">
                          <Form.Label className="text-main fs-13 fw-500 mb-1">
                            Keywords
                          </Form.Label>
                          <Form.Control
                            type="text"
                            placeholder="jordan, yeezy, ev6"
                            value={routerForward.keywords}
                            onChange={(e) =>
                              setRouterForward({
                                ...routerForward,
                                keywords: e.target.value,
                              })
                            }
                            className="form-input-field border rounded py-2 px-3"
                          />
                          {routerForward.keywords && (
                            <small className="text-white-65 fs-12">
                              {
                                routerForward.keywords
                                  .split(",")
                                  .map((k) => k.trim())
                                  .filter(Boolean).length
                              } keyword(s) — any-of match
                            </small>
                          )}
                        </Form.Group>

                        <Form.Group className="mb-3">
                          <Form.Label className="text-main fs-13 fw-500 mb-1">
                            Max Price
                          </Form.Label>
                          <Form.Control
                            type="number"
                            step="0.01"
                            placeholder="130"
                            value={routerForward.max_price}
                            onChange={(e) =>
                              setRouterForward({
                                ...routerForward,
                                max_price: e.target.value,
                              })
                            }
                            className="form-input-field border rounded py-2 px-3"
                          />
                        </Form.Group>

                        <Form.Group className="mb-3">
                          <Form.Label className="text-main fs-13 fw-500 mb-1">
                            Regions
                          </Form.Label>
                          <Form.Control
                            type="text"
                            placeholder="de, uk, fr"
                            value={routerForward.match_regions}
                            onChange={(e) =>
                              setRouterForward({
                                ...routerForward,
                                match_regions: e.target.value,
                              })
                            }
                            className="form-input-field border rounded py-2 px-3"
                          />
                        </Form.Group>
                      </Col>
                      <Col md={6}>
                        <Form.Group className="mb-3">
                          <div className="d-flex align-items-center justify-content-between mb-1">
                            <Form.Label className="text-main fs-13 fw-500 mb-0">
                              Sites
                            </Form.Label>
                            <div className="d-flex gap-2">
                              <Button
                                type="button"
                                variant="link"
                                className="p-0 fs-12 text-decoration-none"
                                onClick={() =>
                                  setRouterForward({
                                    ...routerForward,
                                    match_monitors: monitorList.map(
                                      (m) => m.code_key || m.folder_name
                                    ),
                                  })
                                }
                              >
                                Select all
                              </Button>
                              <Button
                                type="button"
                                variant="link"
                                className="p-0 fs-12 text-decoration-none"
                                onClick={() =>
                                  setRouterForward({
                                    ...routerForward,
                                    match_monitors: [],
                                  })
                                }
                              >
                                Clear
                              </Button>
                            </div>
                          </div>
                          {(() => {
                            // Build {label, value} options from the monitor list
                            // and project the current selection back to that shape
                            // for react-select. Code_key is the canonical value
                            // sent to the backend — it is what the runtime matches
                            // `match_monitors` against; folder_name is the fallback
                            // for monitors that carry no code_key.
                            const options = monitorList.map((m) => ({
                              value: m.code_key || m.folder_name,
                              label: m.name || m.folder_name,
                            }));
                            const selectedValue = options.filter((o) =>
                              routerForward.match_monitors.includes(o.value)
                            );
                            return (
                              <Select
                                isMulti
                                options={options}
                                value={selectedValue}
                                onChange={(picked) =>
                                  setRouterForward({
                                    ...routerForward,
                                    match_monitors: (picked || []).map((p) => p.value),
                                  })
                                }
                                placeholder="Search and select sites…"
                                noOptionsMessage={() => "No sites match"}
                                styles={siteSelectStyles}
                                closeMenuOnSelect={false}
                              />
                            );
                          })()}
                          <small className="text-white-65 fs-12">
                            {routerForward.match_monitors.length === 0
                              ? "All sites (no filter)"
                              : routerForward.match_monitors.length === monitorList.length
                                ? `All ${monitorList.length} sites selected — equivalent to no filter`
                                : `${routerForward.match_monitors.length} of ${monitorList.length} sites selected`}
                          </small>
                        </Form.Group>

                        <Form.Group className="mb-3">
                          <Form.Label className="text-main fs-13 fw-500 mb-1">
                            Pids/Skus
                          </Form.Label>
                          <Form.Control
                            type="text"
                            placeholder="IQ1323, HJ6779"
                            value={routerForward.match_pids}
                            onChange={(e) =>
                              setRouterForward({
                                ...routerForward,
                                match_pids: e.target.value,
                              })
                            }
                            className="form-input-field border rounded py-2 px-3"
                          />
                        </Form.Group>

                        <Form.Group className="mb-3">
                          <Form.Label className="text-main fs-13 fw-500 mb-1">
                            Sizes
                          </Form.Label>
                          <Form.Control
                            type="text"
                            placeholder="43, 44, M"
                            value={routerForward.match_sizes}
                            onChange={(e) =>
                              setRouterForward({
                                ...routerForward,
                                match_sizes: e.target.value,
                              })
                            }
                            className="form-input-field border rounded py-2 px-3"
                          />
                        </Form.Group>

                        <Form.Group className="mb-3">
                          <Form.Label className="text-main fs-13 fw-500 mb-1">
                            Excluded Domains
                          </Form.Label>
                          <Form.Control
                            type="text"
                            placeholder="nike.com, amazon.de"
                            value={routerForward.exclude_domains}
                            onChange={(e) =>
                              setRouterForward({
                                ...routerForward,
                                exclude_domains: e.target.value,
                              })
                            }
                            className="form-input-field border rounded py-2 px-3"
                          />
                        </Form.Group>
                      </Col>
                    </Row>

                    <div className="d-flex gap-2 mt-2">
                      <Button
                        variant="secondary"
                        className="rounded-3 py-2 px-3 fw-600 fs-12"
                        onClick={() =>
                          setRouterForward({
                            ...EMPTY_ROUTER_FORM,
                            client_id: defaultClientId(clients),
                          })
                        }
                      >
                        Cancel
                      </Button>
                      <Button
                        type="submit"
                        disabled={routerSubmitting}
                        className="btn-primary rounded-3 py-2 px-3 fw-600 fs-12"
                        style={{
                          backgroundColor: "#4A4A4A",
                          borderColor: "#4A4A4A",
                          color: "white",
                        }}
                      >
                        {routerSubmitting ? "Saving…" : "Create"}
                      </Button>
                    </div>
                  </Form>
                </Card.Body>
              </Card>
            </Col>
          </Row>
      </div>

      {/* Overview Modal */}
      <ForwardingOverviewModal
        show={showOverview}
        onHide={() => setShowOverview(false)}
        clients={clients}
      />
    </div>
  );
};

export default ForwardingOptions;
